/**
 * @fileoverview Módulo de Polling para verificação periódica de atualizações
 * @module helpers/polling
 * @description Sistema de polling configurável com suporte a callbacks customizáveis,
 * retry logic, tratamento de erros e controle de estado.
 *
 * @example
 * // Uso básico
 * const polling = new PollingService(async () => {
 *   const response = await api.verificarAtualizacoes();
 *   if (response.tem_atualizacao) {
 *     await carregarDados();
 *   }
 * }, 30000);
 *
 * polling.start();
 *
 * @example
 * // Com tratamento de erros customizado
 * const polling = new PollingService(
 *   async () => { return await verificar(); },
 *   30000,
 *   {
 *     debug: true,
 *     maxRetries: 5,
 *     onError: (error) => console.error('Erro:', error)
 *   }
 * );
 */

/**
 * Classe PollingService para execução periódica de callbacks
 *
 * @class
 * @example
 * const polling = new PollingService(callback, 30000, { debug: true });
 * polling.start();
 */
export class PollingService {
  /**
   * Cria uma nova instância do PollingService
   *
   * @param {Function} callback - Função assíncrona a ser executada periodicamente
   * @param {number} [interval=30000] - Intervalo em milissegundos entre as execuções (padrão: 30 segundos)
   * @param {Object} [options={}] - Opções de configuração
   * @param {boolean} [options.debug=false] - Ativa modo debug com logs detalhados
   * @param {number} [options.maxRetries=3] - Número máximo de tentativas em caso de erro
   * @param {number} [options.initialBackoff=1000] - Delay inicial para retry em milissegundos
   * @param {number} [options.maxBackoff=30000] - Delay máximo para retry em milissegundos
   * @param {Function} [options.onError=null] - Callback customizado para tratamento de erros
   * @param {Function} [options.onSuccess=null] - Callback executado após sucesso da execução
   * @param {string} [options.name='PollingService'] - Nome da instância para logs
   *
   * @throws {Error} Se callback não for uma função
   * @throws {Error} Se interval não for um número positivo
   *
   * @example
   * const polling = new PollingService(
   *   async () => await verificarAtualizacoes(),
   *   30000,
   *   { debug: true, maxRetries: 5 }
   * );
   */
  constructor(callback, interval = 30000, options = {}) {
    // Validações
    if (typeof callback !== 'function') {
      throw new Error('PollingService: callback deve ser uma função');
    }

    if (typeof interval !== 'number' || interval <= 0) {
      throw new Error('PollingService: interval deve ser um número positivo');
    }

    // Configurações principais
    this.callback = callback;
    this.interval = interval;
    this.baseInterval = interval; // ✨ MELHORIA #6: Intervalo base para polling adaptativo

    // Opções de configuração
    this.options = {
      debug: options.debug || false,
      maxRetries: options.maxRetries || 3,
      initialBackoff: options.initialBackoff || 1000,
      maxBackoff: options.maxBackoff || 30000,
      onError: options.onError || null,
      onSuccess: options.onSuccess || null,
      name: options.name || 'PollingService',
      // ✨ MELHORIA #6: Opções de polling adaptativo
      adaptivePolling: options.adaptivePolling !== false, // Habilitado por padrão
      minInterval: options.minInterval || 10000, // 10 segundos
      maxInterval: options.maxInterval || 120000, // 2 minutos
    };

    // Estado interno
    this.timerId = null;
    this.retryTimerId = null; // Timer para retry com backoff
    this.isRunning = false;
    this.isPaused = false;
    this.retryCount = 0;
    this.executionCount = 0;
    this.lastExecutionTime = null;
    this.lastError = null;
    this.isExecuting = false;

    // ✨ MELHORIA #6: Estado do polling adaptativo
    this.consecutiveNoChanges = 0;
    this.currentInterval = interval;

    // Previne múltiplas instâncias
    this._instanceId = `${this.options.name}_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;

    this._log('info', '🔧 Instância criada', {
      intervalo: `${this.interval}ms (${Math.round(this.interval / 1000)}s)`,
      maxRetries: this.options.maxRetries,
      adaptivePolling: this.options.adaptivePolling,
      minInterval: this.options.adaptivePolling ? `${Math.round(this.options.minInterval / 1000)}s` : 'N/A',
      maxInterval: this.options.adaptivePolling ? `${Math.round(this.options.maxInterval / 1000)}s` : 'N/A',
      instanceId: this._instanceId.slice(0, 20) + '...'
    });
  }

  /**
   * Inicia o polling
   *
   * @returns {boolean} true se iniciou com sucesso, false se já estava rodando
   *
   * @example
   * if (polling.start()) {
   *   console.log('Polling iniciado');
   * }
   */
  start() {
    if (this.isRunning) {
      this._log('warn', 'Tentativa de iniciar polling já em execução');
      return false;
    }

    this.isRunning = true;
    this.isPaused = false;
    this.retryCount = 0;

    this._log('info', '🚀 Polling iniciado', {
      intervalo: `${this.interval}ms (${Math.round(this.interval / 1000)}s)`,
      maxRetries: this.options.maxRetries,
      adaptivePolling: this.options.adaptivePolling,
      instanceId: this._instanceId
    });

    // Executa imediatamente a primeira vez
    this._executeCallback();

    // Agenda próximas execuções
    this._scheduleNext();

    return true;
  }

  /**
   * Para o polling completamente
   *
   * @example
   * polling.stop();
   */
  stop() {
    if (!this.isRunning) {
      this._log('warn', 'Tentativa de parar polling que não está rodando');
      return;
    }

    this._clearTimer();
    this.isRunning = false;
    this.isPaused = false;

    const tempoTotal = this.lastExecutionTime ? Date.now() - (this.lastExecutionTime - this.interval * this.executionCount) : 0;
    this._log('info', '⏹️  Polling parado', {
      totalExecutions: this.executionCount,
      tempoTotal: `${Math.round(tempoTotal / 1000)}s`,
      lastExecution: this.lastExecutionTime ? new Date(this.lastExecutionTime).toLocaleString() : 'nunca'
    });
  }

  /**
   * Pausa o polling temporariamente (mantém o estado)
   *
   * @returns {boolean} true se pausou com sucesso, false se não estava rodando
   *
   * @example
   * // Pausar quando aba não está visível
   * document.addEventListener('visibilitychange', () => {
   *   if (document.hidden) {
   *     polling.pause();
   *   }
   * });
   */
  pause() {
    if (!this.isRunning) {
      this._log('warn', 'Tentativa de pausar polling que não está rodando');
      return false;
    }

    if (this.isPaused) {
      this._log('warn', 'Polling já está pausado');
      return false;
    }

    this._clearTimer();
    this.isPaused = true;

    this._log('info', '⏸️  Polling pausado', {
      executionCount: this.executionCount,
      ultimaExecucao: this.lastExecutionTime ? `há ${Math.round((Date.now() - this.lastExecutionTime) / 1000)}s` : 'nunca'
    });
    return true;
  }

  /**
   * Retoma o polling após pausar
   *
   * @param {boolean} [executeImmediately=true] - Se deve executar imediatamente ao retomar
   * @returns {boolean} true se retomou com sucesso, false se não estava pausado
   *
   * @example
   * // Retomar quando aba volta a ficar visível
   * document.addEventListener('visibilitychange', () => {
   *   if (!document.hidden) {
   *     polling.resume(true); // Verifica imediatamente
   *   }
   * });
   */
  resume(executeImmediately = true) {
    if (!this.isRunning) {
      this._log('warn', 'Tentativa de retomar polling que não está rodando');
      return false;
    }

    if (!this.isPaused) {
      this._log('warn', 'Polling não está pausado');
      return false;
    }

    this.isPaused = false;
    this._log('info', '▶️  Polling retomado', {
      executeImmediately,
      proximaExecucao: executeImmediately ? 'imediata' : `em ${Math.round(this.currentInterval / 1000)}s`
    });

    if (executeImmediately) {
      this._executeCallback();
    }

    this._scheduleNext();
    return true;
  }

  /**
   * Atualiza o intervalo de polling
   *
   * @param {number} newInterval - Novo intervalo em milissegundos
   * @throws {Error} Se newInterval não for um número positivo
   *
   * @example
   * // Ajustar intervalo baseado em condições
   * if (highActivity) {
   *   polling.updateInterval(10000); // 10 segundos
   * } else {
   *   polling.updateInterval(60000); // 1 minuto
   * }
   */
  updateInterval(newInterval) {
    if (typeof newInterval !== 'number' || newInterval <= 0) {
      throw new Error('PollingService: newInterval deve ser um número positivo');
    }

    const oldInterval = this.interval;
    this.interval = newInterval;

    this._log('info', '⏱️  Intervalo atualizado manualmente', {
      de: `${Math.round(oldInterval / 1000)}s`,
      para: `${Math.round(newInterval / 1000)}s`
    });

    // Se estiver rodando, reagenda com novo intervalo
    if (this.isRunning && !this.isPaused) {
      this._clearTimer();
      this._scheduleNext();
    }
  }

  /**
   * Retorna estatísticas da instância de polling
   *
   * @returns {Object} Objeto com estatísticas
   * @property {string} instanceId - ID único da instância
   * @property {boolean} isRunning - Se está em execução
   * @property {boolean} isPaused - Se está pausado
   * @property {boolean} isExecuting - Se está executando callback no momento
   * @property {number} executionCount - Total de execuções realizadas
   * @property {number|null} lastExecutionTime - Timestamp da última execução
   * @property {Error|null} lastError - Último erro ocorrido
   * @property {number} retryCount - Tentativas de retry atuais
   * @property {number} interval - Intervalo atual em milissegundos
   *
   * @example
   * const stats = polling.getStats();
   * console.log(`Execuções: ${stats.executionCount}`);
   */
  getStats() {
    return {
      instanceId: this._instanceId,
      isRunning: this.isRunning,
      isPaused: this.isPaused,
      isExecuting: this.isExecuting,
      executionCount: this.executionCount,
      lastExecutionTime: this.lastExecutionTime,
      lastError: this.lastError,
      retryCount: this.retryCount,
      interval: this.interval
    };
  }

  /**
   * Força uma execução imediata do callback (fora do ciclo normal)
   *
   * @returns {Promise<void>}
   *
   * @example
   * // Verificar manualmente sob demanda
   * await polling.forceExecution();
   */
  async forceExecution() {
    this._log('info', '⚡ Execução forçada manualmente pelo usuário');
    await this._executeCallback();
  }

  /**
   * Destrói a instância de polling, limpando todos os recursos
   *
   * @example
   * // Em beforeDestroy do componente Vue
   * beforeDestroy() {
   *   this.polling.destroy();
   * }
   */
  destroy() {
    this.stop();
    this.callback = null;
    this._log('info', '🗑️  Instância destruída', {
      totalExecutions: this.executionCount,
      instanciaId: this._instanceId.slice(0, 20) + '...'
    });
  }

  // ==================== MÉTODOS PRIVADOS ====================

  /**
   * Agenda a próxima execução do polling
   * @private
   */
  _scheduleNext() {
    if (!this.isRunning || this.isPaused) {
      return;
    }

    this._clearTimer();

    // ✨ MELHORIA #6: Usa currentInterval (adaptativo) ao invés de interval fixo
    this.timerId = setTimeout(() => {
      this._executeCallback();
      this._scheduleNext();
    }, this.currentInterval);

    const segundos = Math.round(this.currentInterval / 1000);
    const proximaExecucao = new Date(Date.now() + this.currentInterval);
    this._log('debug', `⏰ Próxima execução em ${segundos}s`, {
      horario: proximaExecucao.toLocaleTimeString('pt-BR'),
      intervalo: `${this.currentInterval}ms`,
      executionCount: this.executionCount
    });
  }

  /**
   * Executa o callback com retry logic
   * @private
   */
  async _executeCallback() {
    // Previne execução simultânea
    if (this.isExecuting) {
      this._log('warn', '⚠️  Execução anterior ainda em andamento - aguardando conclusão');
      return;
    }

    this.isExecuting = true;
    const startTime = Date.now();

    this._log('debug', '🔄 Iniciando verificação', {
      execucao: `#${this.executionCount + 1}`,
      horario: new Date().toLocaleTimeString('pt-BR'),
      intervaloAtual: `${this.currentInterval}ms`
    });

    try {
      // Executa o callback do usuário com proteção adicional
      let result;
      try {
        result = await this.callback();
      } catch (callbackError) {
        // Erro no callback do usuário - não deve causar retry infinito
        // Marca como erro mas não incrementa retry count se for erro de aplicação
        this._log('error', '🚫 Erro no callback do usuário', {
          erro: callbackError.message,
          tipo: callbackError.name,
          stack: this.options.debug ? callbackError.stack : undefined
        });

        // Re-lança para ser tratado pelo catch externo
        throw callbackError;
      }

      // Execução bem-sucedida
      this.retryCount = 0;
      this.lastError = null;
      this.executionCount++;
      this.lastExecutionTime = Date.now();

      const duration = Date.now() - startTime;

      // Extrair informações úteis do resultado
      const temAtualizacao = result?.data?.tem_atualizacao ?? result?.tem_atualizacao ?? null;
      const hashAtual = result?.data?.hash_atual ?? result?.hash_atual ?? null;
      const quantidadeReservas = result?.data?.quantidade_reservas ?? result?.quantidade_reservas ?? null;

      // ✨ MELHORIA #6: Polling adaptativo - ajusta intervalo baseado em atividade
      if (this.options.adaptivePolling) {
        this._adjustPollingInterval(result);
      }

      const logData = {
        duracao: `${duration}ms`,
        execucao: `#${this.executionCount}`,
        proximoCheck: `${Math.round(this.currentInterval / 1000)}s`
      };

      // Adiciona informações do resultado se disponíveis
      if (temAtualizacao !== null) {
        logData.temAtualizacao = temAtualizacao ? '✅ SIM' : '⏹️  NÃO';
      }
      if (hashAtual) {
        logData.hash = hashAtual.substring(0, 8) + '...';
      }
      if (quantidadeReservas !== null) {
        logData.quantidadeReservas = quantidadeReservas;
      }

      this._log('debug', '✅ Verificação concluída', logData);

      // Callback de sucesso customizado
      if (this.options.onSuccess) {
        try {
          await this.options.onSuccess({
            duration,
            executionCount: this.executionCount,
            result
          });
        } catch (callbackError) {
          this._log('error', 'Erro no callback onSuccess', callbackError);
        }
      }

    } catch (error) {
      this._handleError(error, startTime);
    } finally {
      this.isExecuting = false;
    }
  }

  /**
   * Trata erros com retry logic e backoff exponencial
   * @private
   * @param {Error} error - Erro ocorrido
   * @param {number} startTime - Timestamp do início da execução
   */
  async _handleError(error, startTime) {
    // ✨ Requisições canceladas são normais (mudança de rota) - não deve fazer retry
    if (error.isCanceled || error.__CANCEL__ || error.message?.includes('canceled')) {
      this._log('debug', '⚠️  Requisição cancelada (esperado - navegou para outra tela)');
      this.retryCount = 0; // Reset contador
      return; // Não faz retry
    }

    // ✨ Timeouts em polling: aumenta intervalo temporariamente
    if (error.isTimeout || error.code === 'ECONNABORTED') {
      this._log('warn', '⏱️  Timeout detectado - servidor demorou para responder');

      // Aumenta intervalo para 2x o atual (até o máximo configurado)
      const novoIntervalo = Math.min(this.currentInterval * 2, this.options.maxInterval);
      if (novoIntervalo !== this.currentInterval) {
        this._log('info', `🔺 Ajustando intervalo: ${Math.round(this.currentInterval / 1000)}s → ${Math.round(novoIntervalo / 1000)}s`);
        this.currentInterval = novoIntervalo;
      }

      // Para timeouts, faz retry apenas 1 vez (ao invés de 3)
      // Próximas verificações vão acontecer no novo intervalo maior
      this.retryCount = Math.min(this.retryCount, this.options.maxRetries - 1);
    }

    this.lastError = error;
    const duration = Date.now() - startTime;

    this._log('error', '❌ Erro na verificação', {
      erro: error.message,
      tipo: error.name,
      codigo: error.code || error.status || 'N/A',
      duracao: `${duration}ms`,
      tentativa: `${this.retryCount}/${this.options.maxRetries}`,
      stack: this.options.debug ? error.stack : undefined
    });

    // Callback de erro customizado
    if (this.options.onError) {
      try {
        await this.options.onError(error, {
          retryCount: this.retryCount,
          maxRetries: this.options.maxRetries,
          duration
        });
      } catch (callbackError) {
        this._log('error', 'Erro no callback onError', callbackError);
      }
    }

    // Retry logic com backoff exponencial
    if (this.retryCount < this.options.maxRetries) {
      this.retryCount++;
      const backoffDelay = this._calculateBackoff();

      this._log('info', `🔄 Agendando retry ${this.retryCount}/${this.options.maxRetries}`, {
        delay: `${backoffDelay}ms (${Math.round(backoffDelay / 1000)}s)`,
        proximaTentativa: new Date(Date.now() + backoffDelay).toLocaleTimeString('pt-BR')
      });

      // Agenda retry e armazena o timer para permitir cancelamento
      this.retryTimerId = setTimeout(async () => {
        this.retryTimerId = null;
        if (this.isRunning && !this.isPaused) {
          await this._executeCallback();
        }
      }, backoffDelay);

    } else {
      this._log('error', '🚫 Número máximo de retries atingido - próxima tentativa no próximo ciclo', {
        maxRetries: this.options.maxRetries,
        ultimoErro: error.message,
        proximoCheck: `${Math.round(this.currentInterval / 1000)}s`
      });

      // Reset do contador para próxima execução agendada
      this.retryCount = 0;
    }
  }

  /**
   * Calcula o delay de backoff exponencial
   * @private
   * @returns {number} Delay em milissegundos
   */
  _calculateBackoff() {
    const exponentialDelay = this.options.initialBackoff * Math.pow(2, this.retryCount - 1);
    const jitter = Math.random() * 1000; // Adiciona jitter de até 1 segundo
    const delay = Math.min(exponentialDelay + jitter, this.options.maxBackoff);

    return Math.floor(delay);
  }

  /**
   * Limpa os timers de polling (principal e retry)
   * @private
   */
  _clearTimer() {
    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }

    // Limpa também o timer de retry para evitar execuções pendentes
    if (this.retryTimerId) {
      clearTimeout(this.retryTimerId);
      this.retryTimerId = null;
    }
  }

  /**
   * Ajusta o intervalo de polling baseado em atividade (MELHORIA #6)
   * @private
   * @param {Object} result - Resultado da execução do callback
   */
  _adjustPollingInterval(result) {
    // Verifica se há indicação de atualização no resultado
    const temAtualizacao = result?.tem_atualizacao ||
                          result?.data?.tem_atualizacao ||
                          false;

    if (temAtualizacao) {
      // Há atividade - reduz intervalo para monitorar mais de perto
      this.consecutiveNoChanges = 0;
      const intervaloAnterior = this.currentInterval;
      this.currentInterval = this.options.minInterval;

      if (intervaloAnterior !== this.currentInterval) {
        this._log('info', '⚡ Atividade detectada - aumentando frequência', {
          de: `${Math.round(intervaloAnterior / 1000)}s`,
          para: `${Math.round(this.currentInterval / 1000)}s`
        });
      }
    } else {
      // Sem mudanças - aumenta intervalo gradualmente (backoff exponencial)
      this.consecutiveNoChanges++;

      // Fórmula: baseInterval * (1.5 ^ consecutiveNoChanges)
      const intervaloAnterior = this.currentInterval;
      const novoIntervalo = Math.min(
        this.baseInterval * Math.pow(1.5, this.consecutiveNoChanges),
        this.options.maxInterval
      );

      this.currentInterval = Math.round(novoIntervalo);

      if (intervaloAnterior !== this.currentInterval) {
        this._log('info', '🐌 Sem alterações - reduzindo frequência', {
          semMudancas: `${this.consecutiveNoChanges}x`,
          de: `${Math.round(intervaloAnterior / 1000)}s`,
          para: `${Math.round(this.currentInterval / 1000)}s`
        });
      }
    }
  }

  /**
   * Sistema de logging com níveis
   * @private
   * @param {string} level - Nível do log (debug, info, warn, error)
   * @param {string} message - Mensagem do log
   * @param {*} [data] - Dados adicionais
   */
  _log(level, message, data = null) {
    // Log de erro sempre é exibido
    if (level === 'error') {
      console.error(`[${this.options.name}] ${message}`, data || '');
      return;
    }

    // Outros logs apenas em modo debug
    if (!this.options.debug) {
      return;
    }

    const timestamp = new Date().toISOString();
    const prefix = `[${this.options.name}][${timestamp}][${level.toUpperCase()}]`;

    const logMethods = {
      debug: console.log,
      info: console.info,
      warn: console.warn
    };

    const logMethod = logMethods[level] || console.log;

    if (data) {
      logMethod(`${prefix} ${message}`, data);
    } else {
      logMethod(`${prefix} ${message}`);
    }
  }
}

/**
 * Factory function para criar instâncias de PollingService
 *
 * @param {Function} callback - Callback a ser executado
 * @param {number} [interval=30000] - Intervalo em milissegundos
 * @param {Object} [options={}] - Opções de configuração
 * @returns {PollingService} Nova instância do PollingService
 *
 * @example
 * const polling = createPollingService(
 *   async () => await verificar(),
 *   30000,
 *   { debug: true }
 * );
 */
export function createPollingService(callback, interval = 30000, options = {}) {
  return new PollingService(callback, interval, options);
}

/**
 * Export default para uso com import default
 */
export default PollingService;
